import { ReactNode } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Tooltip,
  Divider,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material'
import HomeIcon from '@mui/icons-material/Home'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import SchoolIcon from '@mui/icons-material/School'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import NoteIcon from '@mui/icons-material/Note'
import SettingsIcon from '@mui/icons-material/Settings'
import LoginIcon from '@mui/icons-material/Login'
import LogoutIcon from '@mui/icons-material/Logout'
import FeedbackIcon from '@mui/icons-material/Feedback'
import GetAppIcon from '@mui/icons-material/GetApp'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import QueueMusicIcon from '@mui/icons-material/QueueMusic'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth'
import { LEARN_PATH } from '../lib/learnRoutes'
import { useThemeMode } from '../ThemeContext'
import { useKidMode } from '../contexts/KidModeContext'
import { BottomNav } from './BottomNav'
import { BrandMark } from './BrandMark'
import { KidLayout } from './KidLayout'
import LanguageSwitcher from './LanguageSwitcher'
import { PlaylistMiniPlayer } from './PlaylistMiniPlayer'
import { InstallPrompt } from './InstallPrompt'
import { UpdatePrompt } from './UpdatePrompt'
import { InstallAppButton } from './InstallAppButton'
import { FeedbackDialog } from './FeedbackDialog'
import { OfflineBanner } from './OfflineBanner'
import { useState } from 'react'

interface LayoutProps {
  children: ReactNode
}

const RAIL_WIDTH = 72

const publicNavItems = [
  { icon: <HomeIcon />, path: '/', labelKey: 'nav.home' },
  { icon: <MenuBookIcon />, path: '/reader', labelKey: 'nav.reader' },
]

const authNavItems = [
  { icon: <BookmarkIcon />, path: '/bookmarks', labelKey: 'nav.bookmarks' },
  { icon: <SchoolIcon />, path: LEARN_PATH, labelKey: 'nav.learn' },
  { icon: <QueueMusicIcon />, path: '/playlists', labelKey: 'nav.playlists' },
  { icon: <TrendingUpIcon />, path: '/progress', labelKey: 'nav.progress' },
  { icon: <NoteIcon />, path: '/notes', labelKey: 'nav.notes' },
  { icon: <SettingsIcon />, path: '/settings', labelKey: 'nav.settings' },
]

export function Layout({ children }: LayoutProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { user, logout } = useAuth()
  const { mode, toggleMode } = useThemeMode()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { isKidMode } = useKidMode()

  // Feedback dialog — opens from the rail's Feedback icon (desktop) so it
  // doesn't need a floating FAB. BottomNav owns its own feedback entry +
  // dialog instance on mobile (see BottomNav.tsx).
  //
  // Declared BEFORE the kid-mode early return: hooks must run in the same
  // order on every render, and kid mode returns early. With this below the
  // return, leaving kid mode rendered one more hook than the previous render
  // and React threw #310.
  const [feedbackOpen, setFeedbackOpen] = useState(false)

  if (isKidMode) {
    return <KidLayout>{children}</KidLayout>
  }

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  const navItems = user
    ? [...publicNavItems, ...authNavItems]
    : publicNavItems

  // Desktop: icon-only rail sidebar
  const desktopRail = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <List sx={{ flex: 1, py: 1, width: '100%' }}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path !== '/' && location.pathname.startsWith(item.path))

          const label = t(item.labelKey)
          return (
            <ListItem key={item.labelKey} disablePadding sx={{ justifyContent: 'center' }}>
              <Tooltip title={label} placement="right" arrow>
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  selected={isActive}
                  sx={{
                    justifyContent: 'center',
                    py: 1.5,
                    mx: 1,
                    minHeight: 48,
                  }}
                >
                  <ListItemIcon sx={{
                    minWidth: 0,
                    justifyContent: 'center',
                    color: isActive ? 'text.primary' : 'text.secondary',
                  }}>
                    {item.icon}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            </ListItem>
          )
        })}
      </List>

      <Divider sx={{ width: '60%', opacity: 0.6 }} />

      <List sx={{ py: 1, width: '100%' }}>
        {/* Install app — manual entry point. Hides automatically when
            already in standalone mode. */}
        <InstallAppButton>
          {({ onClick, available }) =>
            available ? (
              <ListItem disablePadding sx={{ justifyContent: 'center' }}>
                <Tooltip title={t('install.menuLabel')} placement="right" arrow>
                  <ListItemButton onClick={onClick} sx={{ justifyContent: 'center', mx: 1 }}>
                    <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center', color: 'primary.main' }}>
                      <GetAppIcon />
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ) : null
          }
        </InstallAppButton>
        {/* Feedback — pinned above Login/Logout. Replaces the previously-
            floating FAB; the bottom of the rail is the natural home for low-
            frequency utility actions. */}
        <ListItem disablePadding sx={{ justifyContent: 'center' }}>
          <Tooltip title={t('feedback.tooltip')} placement="right" arrow>
            <ListItemButton
              onClick={() => setFeedbackOpen(true)}
              sx={{ justifyContent: 'center', mx: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center', color: 'text.secondary' }}>
                <FeedbackIcon />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        </ListItem>
        {user ? (
          <ListItem disablePadding sx={{ justifyContent: 'center' }}>
            <Tooltip title={t('nav.logout')} placement="right" arrow>
              <ListItemButton onClick={handleLogout} sx={{ justifyContent: 'center', mx: 1 }}>
                <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center', color: 'text.secondary' }}>
                  <LogoutIcon />
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ) : (
          <ListItem disablePadding sx={{ justifyContent: 'center' }}>
            <Tooltip title={t('nav.login')} placement="right" arrow>
              <ListItemButton
                component={RouterLink}
                to="/auth"
                sx={{ justifyContent: 'center', mx: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center', color: 'text.secondary' }}>
                  <LoginIcon />
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}
      </List>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      {/* AppBar */}
      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar
          variant={isMobile ? 'dense' : 'regular'}
          sx={{
            // iOS notch / Dynamic Island: push toolbar content below the
            // status bar area. On non-notched devices env() resolves to 0px.
            pt: 'var(--safe-top, 0px)',
            minHeight: { xs: `calc(48px + var(--safe-top, 0px))`, sm: `calc(56px + var(--safe-top, 0px))` },
            // Replicate MUI Toolbar's own default gutters (theme.spacing(2)
            // xs / theme.spacing(3) sm+, i.e. 16px / 24px) and add the
            // left/right safe-area inset on top, so content clears a
            // landscape notch/rounded corner. Resolves to the same 16px/24px
            // as the default in portrait, where the insets are 0.
            pl: { xs: 'calc(16px + var(--safe-left, 0px))', sm: 'calc(24px + var(--safe-left, 0px))' },
            pr: { xs: 'calc(16px + var(--safe-right, 0px))', sm: 'calc(24px + var(--safe-right, 0px))' },
          }}
        >
          <Stack
            direction="row"
            spacing={1.25}
            sx={{ alignItems: 'center', flexGrow: 1 }}
            component={RouterLink}
            to="/"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <BrandMark size={isMobile ? 32 : 36} />
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'baseline' }}>
              <Typography
                variant={isMobile ? 'subtitle1' : 'h6'}
                noWrap
                sx={{
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: 'text.primary',
                  fontSize: isMobile ? '1.05rem' : '1.2rem',
                }}
              >
                Qlearn
              </Typography>
              {!isMobile && (
                <Typography
                  noWrap
                  sx={{
                    fontFamily: '"Amiri Quran", "Traditional Arabic", serif',
                    fontSize: '1.05rem',
                    color: 'primary.main',
                    direction: 'rtl',
                    lineHeight: 1,
                  }}
                  aria-hidden
                >
                  ٱقْرَأْ
                </Typography>
              )}
            </Stack>
          </Stack>

          <LanguageSwitcher />
          <Tooltip title={mode === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}>
            <IconButton color="inherit" onClick={toggleMode} size={isMobile ? 'small' : 'medium'}>
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
          {/* Mobile-only login affordance. On desktop the icon rail sidebar
              already shows a Login button at the bottom; mobile has no rail
              and the BottomNav only renders for authenticated users, so
              without this button an unauthenticated mobile visitor has no
              way to reach /auth from anywhere in the app. */}
          {isMobile && !user && (
            <Tooltip title={t('nav.login')}>
              <IconButton
                color="inherit"
                component={RouterLink}
                to="/auth"
                size="small"
                aria-label={t('nav.login')}
              >
                <LoginIcon />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>
      </AppBar>

      {/* Desktop: icon rail sidebar */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: RAIL_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: RAIL_WIDTH,
              boxSizing: 'border-box',
              overflowX: 'hidden',
            },
          }}
        >
          <Toolbar />
          {desktopRail}
        </Drawer>
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // CRITICAL for mobile: flex children default to `min-width: auto`
          // which resolves to `min-content` (the intrinsic minimum width of
          // all descendants). Any descendant whose intrinsic width exceeds
          // the viewport pushes this Box wider than the viewport and the
          // document horizontally scrolls. Setting minWidth: 0 lets the
          // flex child shrink. We deliberately do NOT add overflow-x: hidden
          // here — that implicitly turns this Box into a scroll container
          // (overflow-y becomes auto), which combined with the AppBar's mt
          // shoves the bottom rows behind the fixed BottomNav.
          minWidth: 0,
          // xs: pages own their gutter (edge-to-edge sections can opt out
          // of horizontal padding by setting it to 0 on themselves). On
          // sm+ we keep the breathing room. Split into pl/pr (rather than
          // px) and add the safe-area insets on top of the existing
          // theme.spacing(2)/(3) values (16px/24px) so content clears a
          // landscape notch/rounded corner — inert (same as before) when
          // the insets are 0.
          pl: { xs: 'var(--safe-left, 0px)', sm: 'calc(16px + var(--safe-left, 0px))', md: 'calc(24px + var(--safe-left, 0px))' },
          pr: { xs: 'var(--safe-right, 0px)', sm: 'calc(16px + var(--safe-right, 0px))', md: 'calc(24px + var(--safe-right, 0px))' },
          py: { xs: 1.5, sm: 2, md: 3 },
          width: { md: `calc(100% - ${RAIL_WIDTH}px)` },
          // Match the AppBar's actual Toolbar minHeight so the first page-row
          // doesn't disappear behind the iOS notch AND doesn't leave an empty
          // strip below the AppBar. Toolbar is `dense` (48px) on xs, the sx
          // override bumps to 56px on sm, and `regular` defaults to 64px on
          // md+. safe-top resolves to 0 on non-notched devices.
          mt: {
            xs: 'calc(48px + var(--safe-top, 0px))',
            sm: 'calc(56px + var(--safe-top, 0px))',
            md: '64px',
          },
          // pb here covers the bottom chrome on desktop (just 24px).
          // Mobile uses a dedicated spacer Box below children instead
          // of pb on main, because some browsers / cache states have been
          // observed to under-apply tall calc()-based pb values to a
          // flex-grow child. The spacer is a DOM element with fixed
          // height — much harder for any layout context to ignore.
          pb: 3,
        }}
      >
        <OfflineBanner />
        {children}
        {isMobile && (
          // Bulletproof bottom clearance for mobile: explicit-height spacer
          // that sits AFTER the page content inside the scroll flow. Tall
          // enough to clear BottomNav (~56-90px with safe-area) + AudioPlayer
          // compact (~56px when shown) + a full card-row of breathing room.
          // Hidden affordance — purely structural; doesn't render any pixels
          // the user could click.
          <Box
            aria-hidden
            sx={{
              height: 'calc(180px + var(--safe-bottom, 0px))',
              flexShrink: 0,
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>

      {/* Mobile: bottom navigation */}
      {isMobile && user && <BottomNav />}

      <InstallPrompt />
      <UpdatePrompt />

      {/* Persistent playlist mini-player. Renders only when a playlist is
          bound to PlaylistAudioContext AND the user is NOT on that playlist's
          detail page (which has its own larger player). */}
      <PlaylistMiniPlayer />

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </Box>
  )
}

